import { useEffect } from 'react';
import type { TableBlock } from '@/types/report-payload';
import type { CellPosition, SelectionRange } from './TableSelection';
import { getCellAt } from '@/lib/table-utils';

type UseTableKeyboardShortcutsProps = {
    disabled: boolean;
    selection: SelectionRange | null;
    selectionRect: {
        rowStart: number;
        rowEnd: number;
        colStart: number;
        colEnd: number;
    } | null;
    editingCell: CellPosition | null;
    hasMultiCellSelection: boolean;
    block: TableBlock;
    getOriginPosition: (cellId: string) => CellPosition | null;
    handleMerge: () => void;
    handleUnmerge: () => void;
    handleArrowMove: (direction: 'left' | 'right' | 'up' | 'down', extend?: boolean) => boolean;
    handleTabMove: (direction: 'next' | 'prev') => boolean;
    startEditingCell: (position: CellPosition, initialValue?: string) => void;
    clearSelectedCells: () => void;
    copySelection: () => void;
    selectAll: () => void;
    setSelection: (selection: SelectionRange | null) => void;
};

export const useTableKeyboardShortcuts = ({
    disabled,
    selection,
    selectionRect,
    editingCell,
    hasMultiCellSelection,
    block,
    getOriginPosition,
    handleMerge,
    handleUnmerge,
    handleArrowMove,
    handleTabMove,
    startEditingCell,
    clearSelectedCells,
    copySelection,
    selectAll,
    setSelection,
}: UseTableKeyboardShortcutsProps) => {
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!selection || disabled) return;
            if (event.altKey) return;
            if (
                (event.metaKey || event.ctrlKey) &&
                !['m', 'v', 'c', 'a'].includes(event.key.toLowerCase())
            ) {
                return;
            }

            const target = event.target as HTMLElement | null;
            const isEditableTarget =
                target?.tagName === 'INPUT' ||
                target?.tagName === 'TEXTAREA' ||
                target?.isContentEditable;

            if (editingCell) {
                if (event.key === 'Escape') return;
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                setSelection(null);
                return;
            }

            if (isEditableTarget) return;

            if ((event.key === 'm' || event.key === 'M') && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                if (event.shiftKey) {
                    handleUnmerge();
                } else {
                    handleMerge();
                }
                return;
            }

            if ((event.key === 'c' || event.key === 'C') && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                copySelection();
                return;
            }

            if ((event.key === 'a' || event.key === 'A') && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                selectAll();
                return;
            }

            if (event.key === 'Enter') {
                const rect = selectionRect;
                if (!rect) return;
                event.preventDefault();
                const targetPos: CellPosition = {
                    section: selection.start.section,
                    rowIndex: rect.rowStart,
                    colIndex: rect.colStart,
                };

                const cell = getCellAt(
                    block,
                    targetPos.section,
                    targetPos.rowIndex,
                    targetPos.colIndex,
                );
                if (cell?.mergedInto) {
                    const originPos = getOriginPosition(cell.mergedInto);
                    if (originPos) {
                        startEditingCell(originPos);
                        return;
                    }
                }
                startEditingCell(targetPos);
                return;
            }

            const isPrintableKey = event.key.length === 1 && !event.ctrlKey && !event.metaKey;
            const isClearKey = event.key === 'Backspace' || event.key === 'Delete';

            if (isClearKey && hasMultiCellSelection) {
                event.preventDefault();
                clearSelectedCells();
                return;
            }

            if (isPrintableKey || isClearKey) {
                const rect = selectionRect;
                if (!rect) return;
                event.preventDefault();

                let targetPos: CellPosition = {
                    section: selection.start.section,
                    rowIndex: rect.rowStart,
                    colIndex: rect.colStart,
                };

                const cell = getCellAt(
                    block,
                    targetPos.section,
                    targetPos.rowIndex,
                    targetPos.colIndex,
                );

                if (cell?.mergedInto) {
                    const originPos = getOriginPosition(cell.mergedInto);
                    if (originPos) {
                        targetPos = originPos;
                    }
                }

                startEditingCell(targetPos, isClearKey ? '' : event.key);
                return;
            }

            if (event.key === 'ArrowLeft') {
                const moved = handleArrowMove('left', event.shiftKey);
                if (moved) event.preventDefault();
                return;
            }
            if (event.key === 'ArrowRight') {
                const moved = handleArrowMove('right', event.shiftKey);
                if (moved) event.preventDefault();
                return;
            }
            if (event.key === 'ArrowUp') {
                const moved = handleArrowMove('up', event.shiftKey);
                if (moved) event.preventDefault();
                return;
            }
            if (event.key === 'ArrowDown') {
                const moved = handleArrowMove('down', event.shiftKey);
                if (moved) event.preventDefault();
                return;
            }
            if (event.key === 'Tab') {
                const moved = handleTabMove(event.shiftKey ? 'prev' : 'next');
                if (moved) event.preventDefault();
                return;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
        block,
        clearSelectedCells,
        copySelection,
        disabled,
        editingCell,
        getOriginPosition,
        handleArrowMove,
        handleMerge,
        handleTabMove,
        handleUnmerge,
        hasMultiCellSelection,
        selectAll,
        startEditingCell,
        selection,
        selectionRect,
        setSelection,
    ]);
};
